import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '@astro/api'
import { useToast } from '../../../context/ToastContext'
import { buildOfferings } from './AstrologerOfferingsSection'
import { unwrapSlots } from '../booking/TimeSlotPicker'
import { toIsoDate, formatTimeLabel } from '../../../utils/date'

const WEEKDAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

function buildUpcomingDays(count = 4) {
  const days = []
  const today = new Date()
  for (let i = 0; i < count; i += 1) {
    const d = new Date(today)
    d.setDate(today.getDate() + i)
    days.push({ label: WEEKDAY[d.getDay()], dayNum: d.getDate(), date: d })
  }
  return days
}

export default function AstrologerBookingSidebar({ astro }) {
  const navigate = useNavigate()
  const offerings = useMemo(() => buildOfferings(astro), [astro])
  const days = useMemo(() => buildUpcomingDays(4), [])

  const [offeringKey, setOfferingKey] = useState(offerings[0]?.key)
  const [dayIndex, setDayIndex] = useState(0)
  const [slot, setSlot] = useState(null)
  const { showToast } = useToast()

  const selectedDay = days[dayIndex]
  const isoDate = selectedDay ? toIsoDate(selectedDay.date) : null

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['available-slots', astro.id, isoDate],
    queryFn: ({ signal }) => api.bookings.availableSlots({ astrologer_id: astro.id, date: isoDate }, { signal }),
    enabled: Boolean(astro.id && isoDate),
  })
  const slots = unwrapSlots(data)

  const selectedOffering = offerings.find((o) => o.key === offeringKey) ?? offerings[0]
  const sessionFee = selectedOffering?.price ?? 0
  const total = sessionFee

  const handleSelectDay = (i) => {
    setDayIndex(i)
    setSlot(null)
  }

  const handleConfirm = () => {
    if (!slot) {
      showToast('Please select an available time slot.')
      return
    }
    navigate(`/astrologers/${astro.id}/book`, { state: { selectedDate: selectedDay.date, selectedSlot: slot } })
  }

  return (
    <aside id="book-slot" className="space-y-6 lg:sticky lg:top-28">
      <div className="info-card p-6 space-y-5">
        <h3 className="text-sm uppercase tracking-[0.3em] text-[#8B7355] font-semibold">Book a session</h3>

        <div className="space-y-3">
          <label htmlFor="consultation-type" className="text-[10px] font-bold uppercase tracking-[0.35em] text-[#8B7355]">
            Consultation type
          </label>
          <select
            id="consultation-type"
            value={offeringKey}
            onChange={(e) => setOfferingKey(e.target.value)}
            className="w-full rounded-2xl border border-[#D4AF37]/20 bg-white px-4 py-3 text-sm text-[#1C140A] outline-none focus:border-secondary focus:ring-2 focus:ring-secondary/20"
          >
            {offerings.map((o) => (
              <option key={o.key} value={o.key}>{o.title} — {o.priceLabel}</option>
            ))}
          </select>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="block text-[10px] font-bold uppercase tracking-[0.35em] text-[#8B7355]">Select date</span>
            <button
              type="button"
              onClick={() => navigate(`/astrologers/${astro.id}/book`)}
              className="text-[10px] font-semibold uppercase tracking-wide text-[#1C140A] hover:text-secondary transition-colors"
            >
              More dates →
            </button>
          </div>
          <div className="grid grid-cols-4 gap-2">
            {days.map((d, i) => (
              <button
                key={`${d.label}-${d.dayNum}`}
                type="button"
                onClick={() => handleSelectDay(i)}
                className={`slot-btn${dayIndex === i ? ' active' : ''}`}
              >
                <span className="block text-[9px] uppercase opacity-70">{d.label}</span>{d.dayNum}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          <span className="block text-[10px] font-bold uppercase tracking-[0.35em] text-[#8B7355]">Available slots</span>
          {isPending && (
            <p className="text-xs text-[#8B7355] italic animate-pulse">Checking live availability…</p>
          )}
          {isError && (
            <div className="flex items-center justify-between gap-3 text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-3">
              <span>Couldn&rsquo;t load slots.</span>
              <button type="button" onClick={() => refetch()} className="font-bold uppercase tracking-widest text-[10px] underline shrink-0">Retry</button>
            </div>
          )}
          {!isPending && !isError && slots.length === 0 && (
            <p className="text-xs text-[#8B7355] italic">No slots available on this date — try another day.</p>
          )}
          {!isPending && !isError && slots.length > 0 && (
            <div className="grid grid-cols-3 gap-2">
              {slots.map((s, index) => {
                const time = s.start_time ?? s.time
                const isAvailable = s.available !== false
                const isActive = slot ? (slot.id ?? slot.start_time ?? slot.time) === (s.id ?? time) : false
                return (
                  <button
                    key={s.id ?? time ?? index}
                    type="button"
                    disabled={!isAvailable}
                    onClick={() => setSlot(s)}
                    className={`slot-btn${isActive ? ' active' : ''}${!isAvailable ? ' disabled' : ''}`}
                  >
                    {formatTimeLabel(time)}
                  </button>
                )
              })}
            </div>
          )}
        </div>

        <div className="astro-divider" />

        <div className="flex items-center justify-between text-sm">
          <span className="text-[#5C3A18]">Session fee</span>
          <span className="font-bold text-[#1C140A]">{sessionFee > 0 ? `₹${sessionFee.toLocaleString('en-IN')}` : 'Free'}</span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-[#5C3A18]">Platform fee</span>
          <span className="font-bold text-[#1C140A]">₹0</span>
        </div>
        <div className="astro-divider" />
        <div className="flex items-center justify-between text-base">
          <span className="font-semibold text-[#1C140A]">Total</span>
          <span className="font-bold text-[#C8922A]">{total > 0 ? `₹${total.toLocaleString('en-IN')}` : 'Free'}</span>
        </div>

        <button
          type="button"
          onClick={handleConfirm}
          className="hero-btn-primary w-full inline-flex items-center justify-center rounded-full px-6 py-3.5 text-sm font-semibold uppercase tracking-[0.25em]"
        >
          Confirm booking
        </button>
        <p className="text-center text-[11px] text-[#8B7355]">Free rescheduling up to 4 hours before your session</p>
      </div>

      <div className="info-card p-6 space-y-4">
        <h3 className="text-sm uppercase tracking-[0.3em] text-[#8B7355] font-semibold">Need help choosing?</h3>
        <p className="text-sm leading-relaxed text-[#5C3A18]">Our support team can help match you with the right astrologer and consultation type.</p>
        <a
          href="tel:+919091840899"
          className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-[#D7C29A]/30 bg-white px-6 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-[#1C140A] hover:border-secondary transition-colors"
        >
          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25V16.14a2.25 2.25 0 0 0-1.545-2.14l-4.22-1.354a2.25 2.25 0 0 0-2.586 1.022l-1.411 2.334a15.682 15.682 0 0 1-6.75-6.75l2.334-1.411a2.25 2.25 0 0 0 1.022-2.586l-1.354-4.22A2.25 2.25 0 0 0 6.14 2.25H4.5a2.25 2.25 0 0 0-2.25 2.25v2.25Z" />
          </svg>
          Call +91 90918 40899
        </a>
      </div>
    </aside>
  )
}
